import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    inject,
    Injector,
    linkedSignal,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { BannerComponent, WrongRegionComponent } from '@shs-digi-care-monorepo/frontend-lib/components';
import { EnvironmentHintService } from '@shs-digi-care-monorepo/frontend-lib/services';
import { TeamActions, UserState } from '@shs-digi-care-monorepo/frontend-lib/states';
import { ShSnackbarContainerComponent } from '@shs-digi-care-monorepo/frontend-lib/ui/sh-snackbar/sh-snackbar-container.component';
import { Region, wrongRegionOf } from '@shs-digi-care-monorepo/frontend-lib/util';
import { BusinessServices, IUser } from '@shs-digi-care-monorepo/interfaces';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { filter, take, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { HeaderComponent } from './shared/components/header/header.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { CountrySwitchComponent } from './shared/components/country-switch/country-switch.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { DEFAULT_LANGUAGE } from './app.config';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [
        RouterOutlet,
        FooterComponent,
        HeaderComponent,
        SidebarComponent,
        CountrySwitchComponent,
        ShSnackbarContainerComponent,
        BannerComponent,
        WrongRegionComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit {
    private store = inject(Store);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly injector = inject(Injector);
    protected readonly dialog = inject(MatDialog);

    protected readonly stage = environment.stage;
    protected readonly showEnvironmentHint = inject(EnvironmentHintService).showEnvironmentHint;
    protected readonly me: Signal<IUser | null> = this.store.selectSignal(UserState.me);
    protected readonly sidebarCollapsed: WritableSignal<boolean> = signal(true);
    private readonly userLoadedFromState: Signal<boolean> = this.store.selectSignal(UserState.loaded);
    /**
     * Latches to `true` once the user has been loaded for the first time and never
     * reverts to `false`, so the startup spinner is shown only once and never reappears
     * during navigation or any subsequent state changes.
     */
    protected readonly userLoaded: Signal<boolean> = linkedSignal<boolean, boolean>({
        source: this.userLoadedFromState,
        computation: (loaded, previous) => previous?.value || loaded
    });
    /**
     * Region the user is pointed to when their roles do not cover the EMEA stream,
     * in which case the application is replaced by the wrong region notice.
     */
    protected readonly wrongRegion: Signal<Region | null> = computed(() =>
        wrongRegionOf(this.me(), BusinessServices.OM_EMEA)
    );

    constructor() {
        this.loadTeams();
    }

    public ngOnInit(): void {
        this.setDefaultLanguage();
        this.listenToLanguageChanges();
    }

    private setDefaultLanguage(): void {
        const storedLang = localStorage.getItem('language') ?? DEFAULT_LANGUAGE;
        this.translate.setDefaultLang(DEFAULT_LANGUAGE);
        this.translate.use(storedLang);
    }

    private listenToLanguageChanges(): void {
        this.translate.onLangChange
            .pipe(
                tap((event) => localStorage.setItem('language', event.lang)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadTeams(): void {
        inject(OidcSecurityService)
            .isAuthenticated()
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                tap(() => {
                    this.store.dispatch(new TeamActions.LoadAll());
                })
            )
            .subscribe();
    }
}
